import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface TourStep {
  id: string;
  title: string;
  description: string;
  target?: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to OpsGPT! 🎉',
    description: 'Let me show you around. This quick tour will help you get the most out of your AI-powered operations assistant.',
    position: 'center',
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'This is your command center. View key metrics, pending tasks, and recent activity at a glance.',
    target: '[data-tour="dashboard-stats"]',
    position: 'bottom',
  },
  {
    id: 'chat',
    title: 'AI Chat Assistant',
    description: 'Talk to your AI assistant here. Ask questions, create tasks, or get help with approvals using natural language.',
    target: '[data-tour="chat-link"]',
    position: 'right',
  },
  {
    id: 'tasks',
    title: 'Task Management',
    description: 'Create, assign, and track tasks. Set priorities, due dates, and let AI help you stay organized.',
    target: '[data-tour="tasks-link"]',
    position: 'right',
  },
  {
    id: 'team',
    title: 'Team Management',
    description: 'Manage your team members, track capacity, and assign skills for better task allocation.',
    target: '[data-tour="team-link"]',
    position: 'right',
  },
  {
    id: 'approvals',
    title: 'Approvals Hub',
    description: 'Handle expense requests, time-off requests, and other approvals efficiently.',
    target: '[data-tour="approvals-link"]',
    position: 'right',
  },
  {
    id: 'complete',
    title: "You're All Set! 🚀",
    description: 'Start by chatting with your AI assistant or creating your first task. Need help anytime? Just ask!',
    position: 'center',
  },
];

const TOUR_STORAGE_KEY = 'opsgpt_tour_completed';

interface OnboardingContextType {
  isActive: boolean;
  currentStep: TourStep | null;
  currentStepIndex: number;
  totalSteps: number;
  progress: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  hasCompletedTour: boolean;
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  resetTour: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasCompletedTour, setHasCompletedTour] = useState(true);

  useEffect(() => {
    if (user) {
      const completed = localStorage.getItem(`${TOUR_STORAGE_KEY}_${user.id}`);
      setHasCompletedTour(completed === 'true');
    }
  }, [user]);

  const startTour = useCallback(() => {
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const completeTour = useCallback(() => {
    setIsActive(false);
    setHasCompletedTour(true);
    if (user) {
      localStorage.setItem(`${TOUR_STORAGE_KEY}_${user.id}`, 'true');
    }
  }, [user]);

  const nextStep = useCallback(() => {
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      completeTour();
    }
  }, [currentStepIndex, completeTour]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  const skipTour = useCallback(() => {
    completeTour();
  }, [completeTour]);

  const resetTour = useCallback(() => {
    if (user) {
      localStorage.removeItem(`${TOUR_STORAGE_KEY}_${user.id}`);
      setHasCompletedTour(false);
    }
  }, [user]);

  const currentStep = TOUR_STEPS[currentStepIndex] || null;
  const progress = ((currentStepIndex + 1) / TOUR_STEPS.length) * 100;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === TOUR_STEPS.length - 1;

  return (
    <OnboardingContext.Provider
      value={{
        isActive,
        currentStep,
        currentStepIndex,
        totalSteps: TOUR_STEPS.length,
        progress,
        isFirstStep,
        isLastStep,
        hasCompletedTour,
        startTour,
        nextStep,
        prevStep,
        skipTour,
        resetTour,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
